import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import React from 'react';
import PropTypes from 'prop-types';
import { a as addClassNames, c as classNameProps } from './classNames-6a4763fd.js';
import { a as addSpacingClasses, m as marginProps, p as paddingProps } from './spacing-d13ff368.js';
import { p as props } from './componentEl-33ff2aeb.js';
import Icon from './Icon.js';
import { getContentClasses } from './useSidebarState.js';
import './body-21c22068.js';

function SidebarSection(_ref) {
  let {
    componentEl: Component,
    children,
    className,
    margin,
    padding,
    scrollable
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "children", "className", "margin", "padding", "scrollable"]);

  let cssClasses = ['st_sidebar__section'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });

  if (scrollable) {
    cssClasses.push('st_sidebar__section--scrollable');
  }

  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

SidebarSection.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** margin for section, if any */
  margin: marginProps,

  /** margin for section, if any */
  padding: paddingProps,

  /** whether this section should show scrollbars if too much content */
  scrollable: PropTypes.bool
};
SidebarSection.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: null,
  scrollable: true
};
SidebarSection.displayName = 'Sidebar.Section';

function SidebarFooter(_ref) {
  let {
    componentEl: Component,
    children,
    className,
    margin,
    padding,
    border
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "children", "className", "margin", "padding", "border"]);

  let cssClasses = ['st_sidebar__section', 'st_sidebar__section--sticky-bottom'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  if (border) cssClasses.push('st_sidebar__section--border');
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

SidebarFooter.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** margin, if any */
  margin: marginProps,

  /** margin, if any */
  padding: paddingProps,

  /** whether footer should have a border on the top */
  border: PropTypes.bool
};
SidebarFooter.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: {
    all: 'none'
  },
  border: true
};
SidebarFooter.displayName = 'Sidebar.Footer';

function SidebarHeader(_ref) {
  let {
    componentEl: Component,
    children,
    className,
    margin,
    padding,
    border
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "children", "className", "margin", "padding", "border"]);

  let cssClasses = ['st_sidebar__section', 'st_sidebar__section--sticky-top'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });

  if (border) {
    cssClasses.push('st_sidebar__section--border');
  }

  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

SidebarHeader.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** margin for component, if any */
  margin: marginProps,

  /** padding for component, if any */
  padding: paddingProps,

  /** whether header should have a border on the bottom */
  border: PropTypes.bool
};
SidebarHeader.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: {
    all: 'none'
  },
  border: true
};
SidebarHeader.displayName = 'Sidebar.Header';

function SidebarToggle(_ref) {
  let {
    className,
    margin,
    padding,
    status,
    onCollapse,
    onOpen,
    position
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "className", "margin", "padding", "status", "onCollapse", "onOpen", "position"]);

  let cssClasses = ['st_sidebar__toggle', "st_sidebar__toggle--" + position, "st_sidebar__toggle--" + status];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });

  const getIcon = sidebarStatus => {
    switch (sidebarStatus) {
      case 'open':
        if (position === 'left') return 'caret-left';
        return 'caret-right';

      default:
        if (position === 'left') return 'caret-right';
        return 'caret-left';
    }
  };

  const icon = getIcon(status);

  const handleClick = e => {
    e.preventDefault();

    if (status === 'open' || status === 'opening') {
      onCollapse();
    } else {
      onOpen();
    }
  };

  const label = status === 'open' ? 'close sidebar' : 'open sidebar';
  return /*#__PURE__*/React.createElement("div", {
    className: cssClasses.join(' ')
  }, /*#__PURE__*/React.createElement("button", _extends({
    className: "st_sidebar__togglebtn",
    type: "button",
    "aria-label": label,
    onClick: handleClick
  }, otherProps), /*#__PURE__*/React.createElement(Icon, {
    size: "mlg",
    type: icon
  })));
}

SidebarToggle.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** margin for component, if any */
  margin: marginProps,

  /** padding for component, if any. Overrides default */
  padding: paddingProps,

  /** current status */
  status: PropTypes.oneOf(['open', 'opening', 'collapsed', 'collapsing', 'closed', 'closing']),

  /** event handler when collapse button is clicked */
  onCollapse: PropTypes.func,

  /** event handler when open button is clicked */
  onOpen: PropTypes.func,

  /** position of sidebar */
  position: PropTypes.oneOf(['left', 'right'])
};
SidebarToggle.defaultProps = {
  componentEl: 'button',
  className: null,
  margin: null,
  padding: {
    all: 'xs'
  },
  status: 'open',
  onCollapse: () => null,
  onOpen: () => null,
  position: 'left'
};
SidebarToggle.displayName = 'Sidebar.Toggle';

function SidebarPage(_ref) {
  let {
    componentEl: Component,
    children,
    className,
    margin,
    padding,
    status,
    type,
    position
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "children", "className", "margin", "padding", "status", "type", "position"]);

  let cssClasses = getContentClasses({
    type,
    status,
    position
  });
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

SidebarPage.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** margin for section, if any */
  margin: marginProps,

  /** margin for section, if any */
  padding: paddingProps,

  /** whether sidebar is open (if it's being controlled) */
  status: PropTypes.oneOf(['open', 'closed', 'collapsed', 'opening', 'closing', 'collapsing']),

  /** type of sidebar */
  type: PropTypes.oneOf(['permanent', 'slide', 'push'
  /* 'overlay', */
  ]),

  /** the position of the sidebar */
  position: PropTypes.oneOf(['left', 'right'])
};
SidebarPage.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: {
    all: 'none'
  },
  status: 'open',
  type: 'permanent',
  position: 'left'
};
SidebarPage.displayName = 'Sidebar.Page';

function Sidebar(_ref) {
  let {
    componentEl: Component,
    children,
    className,
    status,
    type,
    componentRef,
    top,
    bottom,
    position,
    border
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "children", "className", "status", "type", "componentRef", "top", "bottom", "position", "border"]);

  let cssClasses = ['st_sidebar', "st_sidebar--" + position];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses.push("st_sidebar--" + status);
  cssClasses.push("st_sidebar--" + type);
  if (border) cssClasses.push('st_sidebar--border');
  const styles = otherProps.style || {};

  if (top !== null) {
    styles['--st-module-sidebar-top'] = top;
  }

  if (bottom !== null) {
    styles['--st-module-sidebar-bottom'] = bottom;
  }

  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: componentRef,
    style: styles,
    className: cssClasses.join(' ')
  }, otherProps), children);
}

Sidebar.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** ref for sidebar element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),

  /** extra CSS classes, if any */
  className: classNameProps,

  /** whether sidebar is open (if it's being controlled) */
  status: PropTypes.oneOf(['open', 'closed', 'collapsed', 'opening', 'closing', 'collapsing']),

  /** type of sidebar */
  type: PropTypes.oneOf(['permanent', 'slide', 'push'
  /* 'overlay', */
  ]),

  /** css override for top position of sidebar */
  top: PropTypes.string,

  /** css override for bottom position of sidebar */
  bottom: PropTypes.string,

  /** whether sidebar should have a border on the side */
  border: PropTypes.bool,

  /** the position of the sidebar */
  position: PropTypes.oneOf(['left', 'right'])
};
Sidebar.defaultProps = {
  componentEl: 'div',
  componentRef: null,
  className: null,
  status: 'open',
  type: 'permanent',
  top: null,
  bottom: null,
  border: true,
  position: 'left'
};
Sidebar.Section = SidebarSection;
Sidebar.Footer = SidebarFooter;
Sidebar.Header = SidebarHeader;
Sidebar.Toggle = SidebarToggle;
Sidebar.Page = SidebarPage;

export default Sidebar;
